import {
  ArrowLeft,
  CheckCircle,
  Clock,
  FileText,
  MapPin,
  Phone,
  Play,
  Upload,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const StaffStatus = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState("Assigned");
  const [seconds, setSeconds] = useState(0);
  const [showUploadSuccess, setShowUploadSuccess] = useState(false);
  const isRunning = status === "Ongoing";

  useEffect(() => {
    let timer;
    if (isRunning) {
      timer = setInterval(() => setSeconds((s) => s + 1), 1000);
    }
    return () => clearInterval(timer);
  }, [isRunning]);

  const formatTime = (sec) => {
    const h = String(Math.floor(sec / 3600)).padStart(2, "0");
    const m = String(Math.floor((sec % 3600) / 60)).padStart(2, "0");
    const s = String(sec % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  const handlePrimaryAction = () => {
    if (status === "Assigned") setStatus("Accepted");
    else if (status === "Accepted") setStatus("Ongoing");
    else if (status === "Ongoing") setStatus("Completed");
  };

  const handleUploadProof = () => {
    console.log("Upload proof clicked");
    setShowUploadSuccess(true);
    setTimeout(() => setShowUploadSuccess(false), 2000);
  };

  const handleCallCustomer = () => {
    console.log("Calling customer...");
  };

  const handleBack = () => {
    console.log("Navigate back to StaffMyJobs");
    navigate(`/my-jobs`);
  };

  const actionLabel = {
    Assigned: "Accept Job",
    Accepted: "Start Work",
    Ongoing: "Complete Job",
    Completed: "Job Completed",
  };

  const statusTitleMap = {
    Assigned: "New Job Assigned",
    Accepted: "Ready to Start",
    Ongoing: "Serving Now",
    Completed: "Job Completed",
  };

  const title = statusTitleMap[status] || "Active Job";

  const statusConfig = {
    Assigned: { color: "bg-blue-600", dotColor: "bg-blue-600" },
    Accepted: { color: "bg-amber-600", dotColor: "bg-amber-600" },
    Ongoing: { color: "bg-green-600", dotColor: "bg-green-600" },
    Completed: { color: "bg-gray-600", dotColor: "bg-gray-600" },
  };

  const statusSteps = ["Assigned", "Accepted", "Ongoing", "Completed"];
  const currentStepIndex = statusSteps.indexOf(status);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-4 p-6 pb-4 border-gray-200 sticky top-0 z-10">
        <h1 className="text-2xl font-bold text-primaryColor">{title}</h1>
      </div>

      <div className="px-4 py-5 space-y-5 pb-24">
        {/* Job Summary Card */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
          {/* Customer Info */}
          <div className="px-4 pt-4 pb-3 border-b border-gray-100">
            <div className="flex justify-between items-start gap-3">
              <div className="flex-1">
                <h2 className="font-semibold text-lg text-gray-900">
                  Anita Sharma
                </h2>
                <p className="text-sm text-gray-600 mt-0.5">
                  Hair Spa & Styling
                </p>
              </div>
              <button
                onClick={handleCallCustomer}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-green-50 active:bg-green-100 transition-colors"
              >
                <Phone size={18} className="text-green-600" />
              </button>
            </div>
          </div>

          {/* Job Details */}
          <div className="px-4 py-3 space-y-2.5">
            {/* Time */}
            <div className="flex items-center gap-2.5 text-sm">
              <div className="flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 flex-shrink-0">
                <Clock size={16} className="text-gray-600" />
              </div>
              <span className="font-medium text-gray-900">
                10:00 AM – 11:30 AM
              </span>
            </div>

            {/* Location */}
            <div className="flex items-center gap-2.5 text-sm">
              <div className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-50 flex-shrink-0">
                <MapPin size={16} className="text-blue-600" />
              </div>
              <span className="font-medium text-gray-700">
                Patia, Bhubaneswar
              </span>
            </div>
          </div>
        </div>

        {/* Timer Card */}
        <div
          className={`rounded-2xl p-5 shadow-sm transition-all ${
            status === "Ongoing" ? "bg-green-600" : "bg-gray-800"
          }`}
        >
          <div className="flex justify-between items-center">
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wider text-white/70 mb-1">
                {status === "Ongoing" ? "Job in Progress" : "Service Timer"}
              </p>
              <p className="text-3xl font-mono font-bold text-white tabular-nums">
                {formatTime(seconds)}
              </p>
              {status === "Ongoing" && (
                <p className="text-xs text-white/70 mt-1">
                  Tap below to complete
                </p>
              )}
            </div>
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-white/10">
              {status === "Accepted" && (
                <Play size={24} className="text-white ml-0.5" />
              )}
              {status === "Ongoing" && (
                <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
              )}
              {status === "Completed" && (
                <CheckCircle size={24} className="text-white" />
              )}
            </div>
          </div>
        </div>

        {/* Status Progress */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
          <p className="text-xs font-medium text-gray-600 mb-3">Job Progress</p>

          {/* Progress Bar */}
          <div className="relative mb-4">
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${statusConfig[status].color}`}
                style={{
                  width: `${
                    ((currentStepIndex + 1) / statusSteps.length) * 100
                  }%`,
                }}
              />
            </div>
          </div>

          {/* Status Labels */}
          <div className="flex justify-between">
            {statusSteps.map((s, index) => {
              const isPast = index <= currentStepIndex;
              const isCurrent = s === status;

              return (
                <div
                  key={s}
                  className="flex flex-col items-center gap-1.5 flex-1"
                >
                  <div
                    className={`w-2.5 h-2.5 rounded-full transition-colors ${
                      isPast ? statusConfig[s].dotColor : "bg-gray-200"
                    }`}
                  />
                  <span
                    className={`text-xs transition-colors ${
                      isCurrent
                        ? "text-gray-900 font-semibold"
                        : "text-gray-500"
                    }`}
                  >
                    {s}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Action Buttons */}
        {status !== "Completed" ? (
          <button
            onClick={handlePrimaryAction}
            className={`w-full h-14 rounded-xl text-white text-base font-semibold transition-all active:scale-[0.98] ${statusConfig[status].color} `}
          >
            {actionLabel[status]}
          </button>
        ) : (
          <div className="space-y-3">
            {showUploadSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                <span className="text-sm font-medium text-green-700">
                  Proof uploaded successfully!
                </span>
              </div>
            )}

            <button
              onClick={handleUploadProof}
              className="w-full h-14 rounded-xl bg-primaryColor active:bg-[#350349] text-white font-semibold flex justify-center items-center gap-2 transition-colors"
            >
              <Upload size={20} />
              Upload Completion Proof
            </button>

            <button className="w-full h-14 rounded-xl border-2 border-gray-300 active:bg-gray-50 font-semibold text-gray-700 flex justify-center items-center gap-2 transition-colors">
              <FileText size={20} />
              Submit Bill (Optional)
            </button>
            <div className="flex w-full justify-center">
              <button
                onClick={handleBack}
                className="h-12 flex items-center gap-2 font-medium text-gray-700 transition-colors"
              >
                <ArrowLeft className="size-4" />
                <span>Back to My Jobs</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffStatus;
